/**
 * ErrorBoundary.tsx
 *
 * Top-level React error boundary for CBOps. Wraps every route under
 * `DashboardLayout` so that a render error inside any page component
 * (e.g. an unhandled `undefined.foo` in a freshly-deployed Supabase
 * schema, a malformed RPC response, a third-party widget bug) shows a
 * friendly recovery card instead of a blank white screen.
 *
 * React's error boundary contract:
 *   - Must be a class component (no functional equivalent yet).
 *   - `getDerivedStateFromError` updates state to render the fallback.
 *   - `componentDidCatch` is the side-effect hook for logging.
 *
 * Behaviour:
 *   - On error: render a dark-themed fallback card with the error
 *     message, a "Try again" reset button, and a "Reload page" button.
 *   - "Try again" clears the boundary's internal `hasError` state so
 *     the children remount — useful when the error is transient
 *     (network blip, race condition).
 *   - "Reload page" forces a full `window.location.reload()` —
 *     escape-hatch for unrecoverable state.
 *   - Errors are also written to `console.error` with a `[ErrorBoundary]`
 *     prefix so the Python developer can spot them in browser devtools.
 *
 * Why one boundary, not many:
 *   We deliberately wrap at the layout level (one boundary for the
 *   whole app) rather than per-page. Per-page boundaries swallow errors
 *   silently, which makes debugging harder during the active build
 *   phase. After public launch (Session 31) we may add per-section
 *   boundaries for better isolation.
 *
 * Python developer note: this is the React equivalent of a Django
 * 500 handler — one shared template, server-wide.
 */

import React from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import './ErrorBoundary.css';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  /**
   * Optional render-prop fallback. If omitted the default dark-themed
   * card is used. Tests can pass a stub here.
   */
  fallback?: (error: Error, reset: () => void) => React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error:    Error | null;
}

export default class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { hasError: false, error: null };

  /** React calls this when a descendant throws during render. */
  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  /** Side-effect hook for logging — runs after `getDerivedStateFromError`. */
  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // Always log so devtools shows the failure in red even if the
    // fallback UI isn't immediately scary enough to draw attention.
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] caught', error, info);
  }

  /** Clear `hasError` so children remount and try again. */
  private reset = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): React.ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    if (this.props.fallback && this.state.error) {
      return this.props.fallback(this.state.error, this.reset);
    }

    return <DefaultFallback error={this.state.error} reset={this.reset} />;
  }
}

// ─── Default fallback ─────────────────────────────────────────────────────────

interface FallbackProps {
  error: Error | null;
  reset: () => void;
}

/**
 * Dark-themed crash recovery card. Matches the rest of the dashboard
 * (#1e2235 panel, teal accents) so the user doesn't feel jarred.
 */
const DefaultFallback: React.FC<FallbackProps> = ({ error, reset }) => {
  const reference = error?.name ?? 'error';
  return (
    <div className="eb-page">
      <div className="eb-card">
        <div className="eb-card__icon">
          <AlertTriangle size={24} aria-hidden />
        </div>

        <h1>Something went wrong</h1>
        <p>It&apos;s not you, it&apos;s us. Your information is safe. Please try again in a moment.</p>

        <div className="eb-card__actions">
          <button onClick={reset} className="eb-card__primary">
            <RefreshCw size={16} aria-hidden />
            Try again
          </button>
          <button onClick={() => { window.location.href = '/'; }} className="eb-card__ghost">
            <Home size={16} aria-hidden />
            Back to home
          </button>
        </div>

        {error && (
          <details className="eb-card__details">
            <summary>Technical details</summary>
            <pre>{error.name}: {error.message}</pre>
          </details>
        )}

        <p className="eb-card__ref">
          If this keeps happening, contact support and mention{' '}
          <span className="eb-card__ref-code">{reference}</span>
        </p>
      </div>
    </div>
  );
};
